/**
 * Single preset row - name, badge, actions
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { Layers, Pencil, Play, Plus, Trash2 } from 'lucide-react';

import { Button, Checkbox, Tooltip } from '@/components/ui';
import type { PreparedDrop } from '@/features/smswiser-campaigns/types/workspace.types';
import type { Preset } from '../shared';
import { RulePreview } from '../rule/RulePreview';

export interface PresetRowProps {
  preset: Preset;
  tagColors?: Record<string, string>;
  selectedDrafts: PreparedDrop[];
  canAddToPreset: boolean;
  onApply: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onAddToPreset?: () => void;
  isApplying: boolean;
  isBusy: boolean;
  selectionMode?: boolean;
  selected?: boolean;
  onToggleSelected?: () => void;
}

export const PresetRow: FC<PresetRowProps> = ({
  preset,
  tagColors,
  selectedDrafts,
  canAddToPreset,
  onApply,
  onEdit,
  onDelete,
  onAddToPreset,
  isApplying,
  isBusy,
  selectionMode = false,
  selected = true,
  onToggleSelected,
}) => (
  <div className="flex items-center justify-between rounded-lg border border-border/60 bg-muted/20 px-3 py-2">
    <div className="flex min-w-0 flex-1 items-center gap-2">
      {selectionMode && (
        <Checkbox
          checked={selected}
          onCheckedChange={() => onToggleSelected?.()}
          aria-label={`Include ${preset.name} in bulk apply`}
          className="shrink-0"
        />
      )}
      <Layers className="h-4 w-4 shrink-0 text-muted-foreground" />
      <span className="truncate text-sm font-medium">{preset.name}</span>
      {preset.folders?.length ? (
        <span className="hidden gap-1 sm:flex">
          {preset.folders.slice(0, 3).map((t) => (
            <span
              key={t.id}
              className="max-w-[5rem] truncate rounded bg-background/80 px-1.5 py-0.5 text-[10px] text-muted-foreground"
              title={t.label}
            >
              {t.label}
            </span>
          ))}
        </span>
      ) : null}
      {preset.presetType === 'rule-based' ? (
        <>
          <Tooltip
            content={
              <RulePreview
                listRule={preset.listRule as Record<string, unknown>}
                tagColors={tagColors}
              />
            }
            contentClassName="bg-card text-foreground border border-border p-0 min-w-[200px] max-w-[360px] shadow-xl"
            side="top"
          >
            <span className="shrink-0 rounded bg-primary/15 px-1.5 py-0.5 text-xs font-medium text-primary cursor-help">
              Rule-based
            </span>
          </Tooltip>
          {preset.countryKey == null && (
            <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
              Public
            </span>
          )}
        </>
      ) : (
        <>
          <span className="shrink-0 text-xs text-muted-foreground">
            ({preset.items.length} drop{preset.items.length !== 1 ? 's' : ''})
          </span>
          {canAddToPreset && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 shrink-0 p-0"
              onClick={onAddToPreset}
              disabled={isBusy}
              aria-label={`Add ${selectedDrafts.length} drop${selectedDrafts.length !== 1 ? 's' : ''} to ${preset.name}`}
            >
              <Plus className="h-3.5 w-3.5" />
            </Button>
          )}
        </>
      )}
    </div>
    <div className="flex shrink-0 items-center gap-1">
      <Button variant="ghost" size="sm" className="h-7 gap-1" onClick={onApply} disabled={isApplying}>
        <Play className="h-3.5 w-3.5" />
        Apply
      </Button>
      <Button
        variant="ghost"
        size="sm"
        className="h-7 gap-1"
        onClick={onEdit}
        disabled={isApplying}
        aria-label={`Edit preset ${preset.name}`}
      >
        <Pencil className="h-3.5 w-3.5" />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        className="h-7 text-destructive hover:text-destructive"
        onClick={onDelete}
        disabled={isApplying}
        aria-label={`Delete preset ${preset.name}`}
      >
        <Trash2 className="h-3.5 w-3.5" />
      </Button>
    </div>
  </div>
);
